import React, { useState, useMemo } from 'react';
import { X, Save, Calendar } from 'lucide-react';
import { PrimaryButton, SecondaryButton } from './buttons';
import { DeliveryCard } from '@dexco/sales-schedule-chart';
import { toast } from 'react-hot-toast';

// Utility functions for date handling
const getLocalDateString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const parseLocalDate = (dateString: string): Date => {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
};

// Utility function for vendor name matching
const isVendorMatch = (userName: string, vendorName: string): boolean => {
    // Normalize function to handle special characters and spaces
    const normalize = (str: string) => str
        .toLowerCase()
        .trim()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Remove accents
        .replace(/\s+/g, ' '); // Normalize spaces

    const normalizedUserName = normalize(userName);
    const normalizedVendorName = normalize(vendorName);

    // Try exact match first
    if (normalizedUserName === normalizedVendorName) {
        return true;
    }

    // If no exact match, try partial match
    if (normalizedUserName.includes(normalizedVendorName) ||
        normalizedVendorName.includes(normalizedUserName)) {
        return true;
    }

    // If still no match, try word-by-word matching
    if (normalizedUserName.length > 0 && normalizedVendorName.length > 0) {
        const userWords = normalizedUserName.split(' ').filter(word => word.length > 2);
        const vendorWords = normalizedVendorName.split(' ').filter(word => word.length > 2);
        return userWords.some(userWord =>
            vendorWords.some(vendorWord =>
                userWord === vendorWord ||
                userWord.includes(vendorWord) ||
                vendorWord.includes(userWord)
            )
        );
    }

    return false;
};

interface UnprioritizedOrdersModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (prioritizedOrders: DeliveryCard[]) => void;
    allOrders: DeliveryCard[];
    isLoading?: boolean;
    userInfo?: { id: number; email: string; full_name: string; profile_type: 'diretor' | 'vendedor' } | null;
    getQuotaForDate?: (date: string) => number | null;
    monthState?: Array<{ date: string; deliveries: DeliveryCard[] }>;
}

export const UnprioritizedOrdersModal: React.FC<UnprioritizedOrdersModalProps> = ({
    isOpen,
    onClose,
    onSave,
    allOrders,
    isLoading = false,
    userInfo,
    getQuotaForDate,
    monthState
}) => {
    const [prioritizedOrders, setPrioritizedOrders] = useState<DeliveryCard[]>([]);
    const [isSaving, setIsSaving] = useState(false);

    // Get unprioritized orders (orders without dt_producao or with null/empty dt_producao)
    // Filter by user permissions - only show orders the user can modify
    const unprioritizedOrders = useMemo(() => {
        return allOrders.filter(order => {
            const hasNoDate = !order.dt_producao ||
                order.dt_producao === '' ||
                order.dt_producao === 'null' ||
                order.dt_producao === null ||
                order.dt_producao === undefined;

            if (!hasNoDate) return false;

            // For limited users (vendedores), only show their own orders
            if (userInfo?.profile_type === 'vendedor') {
                return isVendorMatch(userInfo.full_name, order.no_vendedor || '');
            }

            // Directors can see all orders
            return true;
        });
    }, [allOrders, userInfo]);

    // Generate available dates (current month, previous month, next month)
    const availableDates = useMemo(() => {
        const dates: string[] = [];
        const currentDate = new Date();

        // Previous month
        const prevMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
        for (let i = 0; i < new Date(prevMonth.getFullYear(), prevMonth.getMonth() + 1, 0).getDate(); i++) {
            const date = new Date(prevMonth.getFullYear(), prevMonth.getMonth(), i + 1);
            dates.push(getLocalDateString(date));
        }

        // Current month
        const currentMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        for (let i = 0; i < new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate(); i++) {
            const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i + 1);
            dates.push(getLocalDateString(date));
        }

        // Next month
        const nextMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
        for (let i = 0; i < new Date(nextMonth.getFullYear(), nextMonth.getMonth() + 1, 0).getDate(); i++) {
            const date = new Date(nextMonth.getFullYear(), nextMonth.getMonth(), i + 1);
            dates.push(getLocalDateString(date));
        }

        return dates;
    }, []);

    // Validation functions
    const isDateBeforeToday = (dateString: string): boolean => {
        try {
            const selectedDate = parseLocalDate(dateString);
            const today = new Date();
            today.setHours(0, 0, 0, 0); // Reset time to compare only dates
            return selectedDate < today;
        } catch (error) {
            console.warn('Error parsing date for validation:', error);
            return false;
        }
    };



    const validateDateAssignment = (card: DeliveryCard, newDate: string): boolean => {
        // Check if date is in the past
        if (isDateBeforeToday(newDate)) {
            toast.error('Não é possível alocar pedidos para datas anteriores à hoje');
            return false;
        }

        // Note: Permission check is now handled at the filter level
        // Only cards the user can modify are shown in the table

        // Check quota limits for limited users
        if (userInfo?.profile_type === 'vendedor' && getQuotaForDate && monthState) {
            const quota = getQuotaForDate(newDate);
            if (quota !== null) {
                // Calculate current volume in target date for this vendor only
                const targetDay = monthState.find(d => d.date === newDate);
                const userFullName = userInfo.full_name;
                const currentVolume = targetDay?.deliveries
                    .filter(c => {
                        if (!c || typeof c.units !== 'number') return false;
                        // Only count cards from the same vendor
                        return isVendorMatch(userFullName, c.no_vendedor || '');
                    })
                    .reduce((sum, c) => sum + c.units, 0) || 0;

                // Add volume from other prioritized orders for the same date
                const sameVendorPrioritizedVolume = prioritizedOrders
                    .filter(order => {
                        if (order.dt_producao !== newDate || order.id === card.id) return false;
                        return isVendorMatch(userFullName, order.no_vendedor || '');
                    })
                    .reduce((sum, order) => sum + (order.units || 0), 0);

                // Check if adding this card would exceed quota
                const totalExistingVolume = currentVolume + sameVendorPrioritizedVolume;
                const newTotalVolume = totalExistingVolume + (card.units || 0);

                if (newTotalVolume > quota) {
                    toast.error(`Operação bloqueada: limite de ${quota.toFixed(2)} m³ seria excedido (atual: ${totalExistingVolume.toFixed(2)} m³ + ${(card.units || 0).toFixed(2)} m³ = ${newTotalVolume.toFixed(2)} m³)`);
                    return false;
                }
            }
        }

        return true;
    };

    // Handle date change for an order
    const handleDateChange = (orderId: string, newDate: string) => {
        const order = unprioritizedOrders.find(o => o.id === orderId);
        if (!order) return;

        // Validate before proceeding
        if (!validateDateAssignment(order, newDate)) {
            return;
        }

        const updatedOrder: DeliveryCard = {
            ...order,
            dt_producao: newDate
        };

        setPrioritizedOrders(prev => {
            const existing = prev.find(p => p.id === orderId);
            if (existing) {
                return prev.map(p => p.id === orderId ? updatedOrder : p);
            } else {
                return [...prev, updatedOrder];
            }
        });

        toast.success(`Pedido ${order.ordem} alocado para ${formatDate(newDate)}`);
    };

    // Handle save
    const handleSave = async () => {
        if (prioritizedOrders.length === 0) {
            toast('Nenhum pedido foi priorizado', { icon: '⚠' });
            return;
        }

        setIsSaving(true);

        try {
            toast.loading('Salvando pedidos priorizados...', { id: 'save-orders' });
            await onSave(prioritizedOrders);
            toast.success(`${prioritizedOrders.length} pedidos salvos com sucesso!`, { id: 'save-orders' });

            // Clear prioritized orders and close modal
            setPrioritizedOrders([]);
            onClose();
        } catch (error) {
            console.error('Error saving prioritized orders:', error);
            toast.error('Erro ao salvar pedidos priorizados', { id: 'save-orders' });
        } finally {
            setIsSaving(false);
        }
    };

    // Handle close
    const handleClose = () => {
        setPrioritizedOrders([]);
        onClose();
    };

    // Format date for display
    const formatDate = (dateString: string) => {
        if (!dateString) return '-';
        try {
            const date = parseLocalDate(dateString);
            return date.toLocaleDateString('pt-BR');
        } catch {
            return dateString;
        }
    };

    // Get product description
    const getProductDescription = (order: DeliveryCard) => {
        const breve = order.prd_tx_breve || '';
        const espessura = order.prd_espessura || order.espessura || '';
        if (breve && espessura) return `${breve} ${espessura}mm`;
        if (breve) return breve;
        if (espessura) return `${espessura}mm`;
        return '';
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/50 transition-opacity duration-200"
                style={{
                    backdropFilter: 'blur(4px)',
                    zIndex: 99998
                }}
                onClick={handleClose}
            />

            {/* Modal */}
            <div
                className="fixed inset-0 flex items-start justify-center p-4 overflow-y-auto"
                style={{
                    zIndex: 99999,
                    paddingTop: '2rem',
                    paddingBottom: '2rem'
                }}
                onClick={(e) => {
                    if (e.target === e.currentTarget) {
                        handleClose();
                    }
                }}
            >
                <div className="relative w-full max-w-7xl my-auto">
                    <div
                        className="bg-white rounded-xl shadow-2xl w-full relative flex flex-col"
                        style={{
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(0, 0, 0, 0.05)',
                            maxHeight: 'calc(100vh - 4rem)'
                        }}
                    >
                        {/* Header */}
                        <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-cyan-50">
                            <div className="flex justify-between items-center">
                                <div className="min-w-0 flex-1 mr-4">
                                    <h2 className="text-xl font-semibold text-gray-900">
                                        Pedidos Não Priorizados
                                    </h2>
                                    <p className="text-sm text-gray-600">
                                        {unprioritizedOrders.length} pedidos aguardando priorização
                                    </p>
                                </div>
                                <button
                                    onClick={handleClose}
                                    className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-white/70 rounded-full transition-colors duration-200"
                                    aria-label="Fechar modal"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto p-6">
                            {isLoading ? (
                                <div className="text-center py-12">
                                    <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                                        Carregando pedidos não priorizados...
                                    </h3>
                                    <p className="text-gray-600">
                                        Aguarde enquanto buscamos os dados.
                                    </p>
                                </div>
                            ) : unprioritizedOrders.length === 0 ? (
                                <div className="text-center py-12">
                                    <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                                        Todos os pedidos estão priorizados
                                    </h3>
                                    <p className="text-gray-600">
                                        Não há pedidos aguardando definição de data de produção.
                                    </p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full border-collapse">
                                        <thead>
                                            <tr className="bg-gray-50 border-b border-gray-200">
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Cliente
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Ordem
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Item
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Data Emissão
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Data Remessa
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Volume não priorizado
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Data Produção
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {unprioritizedOrders.map((order) => {
                                                const isPrioritized = prioritizedOrders.some(p => p.id === order.id);
                                                const prioritizedOrder = prioritizedOrders.find(p => p.id === order.id);

                                                return (
                                                    <tr
                                                        key={order.id}
                                                        className={`hover:bg-gray-50 ${isPrioritized ? 'bg-green-50' : ''}`}
                                                    >
                                                        <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                                                            {order.cli_razao_social}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-900">
                                                            #{order.ordem}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-900">
                                                            {getProductDescription(order)}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-900">
                                                            {formatDate(order.dt_emissao || '')}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-900">
                                                            {formatDate(order.dt_desejada_remessa || '')}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-900 font-semibold">
                                                            {(order.units || order.volume || 0).toFixed(2)} m³
                                                        </td>
                                                        <td className="px-4 py-3 text-sm">
                                                            <select
                                                                value={prioritizedOrder?.dt_producao || ''}
                                                                onChange={(e) => handleDateChange(order.id, e.target.value)}
                                                                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-sm ${isPrioritized
                                                                    ? 'border-green-300 focus:ring-green-500 bg-green-50'
                                                                    : 'border-gray-300 focus:ring-blue-500'
                                                                    }`}
                                                                disabled={isSaving}
                                                            >
                                                                <option value="">Selecione uma data</option>
                                                                {availableDates.map((date) => (
                                                                    <option key={date} value={date}>
                                                                        {(() => {
                                                                            const localDate = parseLocalDate(date);
                                                                            return localDate.toLocaleDateString('pt-BR', {
                                                                                weekday: 'short',
                                                                                day: '2-digit',
                                                                                month: 'short'
                                                                            });
                                                                        })()}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            {isPrioritized && (
                                                                <div className="mt-1 text-xs text-green-600 font-medium">
                                                                    ✓ Priorizado para {formatDate(prioritizedOrder?.dt_producao || '')}
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        {/* Footer - Sticky */}
                        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
                            <div className="text-sm text-gray-600">
                                {prioritizedOrders.length > 0 && (
                                    <span className="text-green-600 font-medium">
                                        {prioritizedOrders.length} {prioritizedOrders.length === 1 ? 'pedido priorizado' : 'pedidos priorizados'}
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-4">
                                <SecondaryButton
                                    onClick={handleClose}
                                    disabled={isSaving}
                                >
                                    Cancelar
                                </SecondaryButton>
                                <PrimaryButton
                                    onClick={handleSave}
                                    disabled={isLoading || isSaving || prioritizedOrders.length === 0}
                                    leftIcon={isSaving ?
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> :
                                        <Save className="w-4 h-4" />
                                    }
                                >
                                    {isSaving ? 'Salvando...' : `Salvar ${prioritizedOrders.length > 0 ? `(${prioritizedOrders.length})` : ''}`}
                                </PrimaryButton>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}; 